import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { AppDispatch } from 'store';
import { getEmployeeGoalAssignments } from 'store/Admin';
import {
  Typography,
  ButtonPro,
  TextInputFormik,
  TextAreaPro,
  DatePickerPro,
  Spinner
} from 'components/atoms';
import { AdminHeader, AdminSidebar, SupervisorSelectModal } from 'components';
import { Formik, Form } from 'formik';
import { request } from 'services/apis';
import { toast } from 'react-toastify';
import moment from 'moment';
import './index.scss';

interface Goal {
  id?: string;
  title: string;
  description: string;
  endDate: string;
  status?: string;
}

interface FormValues {
  title: string;
  description: string;
  endDate: string;
}

export const EmployeeGoalManagement = () => {
  const location = useLocation();
  const employee = location.state?.employee;
  const existingAssignment = location.state?.existingAssignment;
  const isEditMode = location.state?.isEdit || false;
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [goals, setGoals] = useState<Goal[]>([]);
  const [showSupervisorModal, setShowSupervisorModal] = useState(false);
  const [selectedSupervisors, setSelectedSupervisors] = useState<
    {
      id: string;
      name: string;
      email: string;
      department: string;
    }[]
  >([]);
  const [supervisorError, setSupervisorError] = useState<string | null>(null);
  const [publishSuccess, setPublishSuccess] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [editingGoal, setEditingGoal] = useState<Goal | null>(null);
  const [deletingGoalId, setDeletingGoalId] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  useEffect(() => {
    if (isEditMode && existingAssignment) {
      const existingGoals: Goal[] =
        existingAssignment.goals?.map((goal: any) => ({
          id: goal.id,
          title: goal.title,
          description: goal.description,
          endDate: goal.endDate,
          status: goal.status
        })) || [];

      setGoals(existingGoals);

      const existingSupervisors =
        existingAssignment.supervisors?.map((supervisor: any) => ({
          id: supervisor.id,
          name: supervisor.name,
          email: supervisor.email,
          department: supervisor.department || supervisor.designation || ''
        })) || [];

      setSelectedSupervisors(existingSupervisors);

      setHasUnsavedChanges(false);
    }
  }, [isEditMode, existingAssignment]);

  const handleAddGoal = (
    values: FormValues,
    { resetForm }: { resetForm: () => void }
  ) => {
    if (!values.title.trim()) {
      return;
    }

    const newGoal: Goal = {
      id: `GOAL-${Date.now()}`,
      title: values.title.trim(),
      description: values.description.trim(),
      endDate: moment(values.endDate).format('YYYY-MM-DD')
    };

    setGoals((prev) => [...prev, newGoal]);
    setHasUnsavedChanges(true);
    resetForm();
  };

  const handleDeleteGoal = async (goalId: string | undefined) => {
    if (!goalId || deletingGoalId) return;

    setDeletingGoalId(goalId);

    try {
      await request.delete(`/GoalAssignment/delete-goal/${goalId}`);

      setGoals((prev) => prev.filter((goal) => goal.id !== goalId));
      setHasUnsavedChanges(true);

      if (editingGoal && editingGoal.id === goalId) {
        setEditingGoal(null);
      }

      if (employee?.userRoleId) {
        dispatch(getEmployeeGoalAssignments(employee.userRoleId));
      }

      toast.success('Goal deleted successfully!');
    } catch (error: any) {
      console.error('Error deleting goal:', error);
      const errorMessage =
        error?.response?.data?.message ||
        'Failed to delete goal. Please try again.';
      toast.error(errorMessage);
    } finally {
      setDeletingGoalId(null);
    }
  };

  const handleEditGoal = (goal: Goal) => {
    setEditingGoal(goal);
  };

  const handleUpdateGoal = (
    values: FormValues,
    { resetForm }: { resetForm: () => void }
  ) => {
    if (!editingGoal || !values.title.trim()) {
      return;
    }

    const updatedGoal: Goal = {
      ...editingGoal,
      title: values.title.trim(),
      description: values.description.trim(),
      endDate: moment(values.endDate).format('YYYY-MM-DD')
    };

    setGoals((prev) =>
      prev.map((goal) => (goal.id === editingGoal.id ? updatedGoal : goal))
    );

    setHasUnsavedChanges(true);
    setEditingGoal(null);
    resetForm();
  };

  const handleCancelEdit = () => {
    setEditingGoal(null);
  };

  const handleSupervisorSelect = (supervisors: any[]) => {
    setSelectedSupervisors(
      supervisors.map((supervisor) => ({
        id: supervisor.id,
        name: supervisor.name,
        email: supervisor.email,
        department: supervisor.department
      }))
    );
    setHasUnsavedChanges(true);
    setSupervisorError(null);
    setShowSupervisorModal(false);
  };

  const handlePublishGoals = async () => {
    if (selectedSupervisors.length === 0) {
      setSupervisorError('Please select at least one supervisor');
      return;
    }
    if (goals.length === 0) {
      setSupervisorError('Please add at least one goal');
      return;
    }

    setIsPublishing(true);
    setSupervisorError(null);

    try {
      if (isEditMode && existingAssignment?.id) {
        const existingGoals = goals.filter(
          (goal) => goal.id && !goal.id.startsWith('GOAL-')
        );
        const newGoals = goals.filter(
          (goal) => !goal.id || goal.id.startsWith('GOAL-')
        );

        const payload = {
          employeeId: employee?.userRoleId || employee?.id,
          supervisorIds: selectedSupervisors.map((supervisor) => supervisor.id),
          goals: existingGoals.map((goal) => ({
            id: goal.id,
            title: goal.title,
            description: goal.description,
            endDate: goal.endDate,
            status: goal.status || 'Pending'
          })),
          goalsToAdd: newGoals.map((goal) => ({
            title: goal.title,
            description: goal.description,
            endDate: goal.endDate,
            status: goal.status || 'Pending'
          }))
        };

        await request.put(
          `/GoalAssignment/update/${existingAssignment.id}`,
          payload
        );
        toast.success('Goals updated successfully!');
      } else {
        const payload = {
          employeeId: employee?.userRoleId || employee?.id,
          supervisorIds: selectedSupervisors.map((supervisor) => supervisor.id),
          goals: goals.map((goal) => ({
            title: goal.title,
            description: goal.description,
            endDate: goal.endDate
          }))
        };

        await request.post('/GoalAssignment/create', payload);
        toast.success('Goals published successfully!');
      }

      if (employee?.userRoleId) {
        dispatch(getEmployeeGoalAssignments(employee.userRoleId));
      }

      setGoals([]);
      setSelectedSupervisors([]);
      setEditingGoal(null);
      setHasUnsavedChanges(false);
      setPublishSuccess(true);
      setTimeout(() => setPublishSuccess(false), 3000);
    } catch (error: any) {
      console.error('Error publishing/updating goals:', error);
      const errorMessage =
        error?.response?.data?.message ||
        `Failed to ${isEditMode ? 'update' : 'publish'} goals. Please try again.`;
      setSupervisorError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsPublishing(false);
    }
  };

  return (
    <>
      <AdminHeader />
      <div className="dashboard-wrapper">
        <AdminSidebar />
        <div
          className="employee-goal-management-wrapper"
          style={{ paddingBottom: '60px' }}
        >
          <div className="container">
            <div className="back-button">
              <ButtonPro
                label="Back to Employee List"
                onClick={() => navigate(-1)}
                type="white"
                icon="ri-arrow-left-line"
              />
            </div>

            <div className="employee-goal-header">
              <Typography
                type="heading4"
                text={`Goal Management - ${employee?.name || 'Employee'}`}
                weight="bold"
              />
              <Typography
                type="small_regular"
                text={
                  isEditMode
                    ? `Edit and manage performance goals for ${employee?.name || 'Employee'}`
                    : `Set and manage performance goals for ${employee?.name || 'Employee'}`
                }
                color="gray"
              />
            </div>

            <div className="supervisor-publish-section">
              <div className="supervisor-select">
                {selectedSupervisors.length > 0 ? (
                  <div className="selected-supervisors-container">
                    <div className="selected-supervisors-header">
                      <Typography
                        type="base_medium"
                        text={`${selectedSupervisors.length} Supervisor${selectedSupervisors.length > 1 ? 's' : ''} Selected`}
                        weight="bold"
                      />
                      <button
                        className="change-supervisors-btn"
                        onClick={() => setShowSupervisorModal(true)}
                        type="button"
                      >
                        Change Selection
                      </button>
                    </div>
                    <div className="selected-supervisors-list">
                      {selectedSupervisors.map((supervisor, index) => (
                        <div
                          key={supervisor.id}
                          className="selected-supervisor-item"
                        >
                          <div className="supervisor-avatar">
                            {supervisor.name
                              .split(' ')
                              .map((name) => name.charAt(0))
                              .join('')
                              .slice(0, 2)
                              .toUpperCase()}
                          </div>
                          <div className="supervisor-info">
                            <Typography
                              type="small_medium"
                              text={supervisor.name}
                              weight="bold"
                            />
                            <Typography
                              type="small_regular"
                              text={supervisor.email}
                              color="gray"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <ButtonPro
                    label="Select Supervisors"
                    type="primary"
                    onClick={() => setShowSupervisorModal(true)}
                    icon="ri-user-add-line"
                  />
                )}
              </div>

              <div className="publish-actions">
                {hasUnsavedChanges && (
                  <div className="unsaved-changes-alert">
                    <Typography
                      type="small_regular"
                      text="⚠️ You have unsaved changes"
                      className="unsaved-changes-text"
                    />
                  </div>
                )}
                <ButtonPro
                  label={
                    isPublishing
                      ? isEditMode
                        ? 'Updating...'
                        : 'Publishing...'
                      : isEditMode
                        ? 'Update Goals'
                        : 'Publish Goals'
                  }
                  type="primary"
                  onClick={handlePublishGoals}
                  disabled={
                    goals.length === 0 ||
                    selectedSupervisors.length === 0 ||
                    isPublishing
                  }
                  icon={
                    isPublishing
                      ? undefined
                      : isEditMode
                        ? 'ri-refresh-line'
                        : 'ri-send-plane-line'
                  }
                />
                {isPublishing && <Spinner showSpinner={true} />}
                {supervisorError && (
                  <Typography
                    type="small_regular"
                    text={supervisorError}
                    className="status-message error"
                  />
                )}
                {publishSuccess && (
                  <Typography
                    type="small_regular"
                    text={
                      isEditMode
                        ? 'Goals updated successfully!'
                        : 'Goals published successfully!'
                    }
                    className="status-message success"
                  />
                )}
              </div>
            </div>

            <div className="goal-management-content">
              <div className="goal-assignment-section">
                <Typography
                  type="heading5"
                  text={editingGoal ? 'Edit Goal' : 'Add New Goal'}
                  weight="bold"
                />

                <Formik
                  key={editingGoal?.id || 'new'}
                  initialValues={{
                    title: editingGoal?.title || '',
                    description: editingGoal?.description || '',
                    endDate: editingGoal?.endDate || new Date().toISOString()
                  }}
                  onSubmit={editingGoal ? handleUpdateGoal : handleAddGoal}
                  validate={(values) => {
                    const errors: Partial<FormValues> = {};
                    if (!values.title.trim()) {
                      errors.title = 'Goal title is required';
                    } else if (values.title.length > 255) {
                      errors.title =
                        'Goal title must not exceed 255 characters';
                    }

                    if (!values.description.trim()) {
                      errors.description = 'Goal description is required';
                    } else if (values.description.length > 1000) {
                      errors.description =
                        'Goal description must not exceed 1000 characters';
                    }

                    if (!editingGoal && !values.endDate)
                      errors.endDate = 'Target completion date is required';
                    return errors;
                  }}
                >
                  {({ errors, touched, isValid, dirty, values }) => (
                    <Form className="goal-form">
                      <div className="form-row">
                        <TextInputFormik
                          name="title"
                          label="Goal Title"
                          placeholder="Enter a clear, measurable goal"
                          required
                          error={Boolean(touched.title && errors.title)}
                        />
                        <div className="character-count">
                          <Typography
                            type="small_regular"
                            text={`${values.title.length}/255 characters`}
                            color={values.title.length > 255 ? 'red' : 'gray'}
                          />
                        </div>
                      </div>

                      <div className="form-row">
                        <TextAreaPro
                          name="description"
                          label="Goal Description"
                          placeholder="Describe the goal, success criteria, and any relevant metrics"
                          required
                        />
                        <div className="character-count">
                          <Typography
                            type="small_regular"
                            text={`${values.description.length}/1000 characters`}
                            color={
                              values.description.length > 1000 ? 'red' : 'gray'
                            }
                          />
                        </div>
                      </div>

                      <div className="form-row">
                        <DatePickerPro
                          name="endDate"
                          label="Target Completion Date"
                          required={!editingGoal}
                        />
                      </div>

                      <div className="form-actions">
                        <ButtonPro
                          label={editingGoal ? 'Save Goal' : 'Add Goal'}
                          type="primary"
                          htmlType="submit"
                          disabled={editingGoal ? !isValid : !isValid || !dirty}
                          icon={editingGoal ? 'ri-save-line' : 'ri-add-line'}
                        />
                        {editingGoal && (
                          <ButtonPro
                            label="Cancel"
                            type="white"
                            onClick={handleCancelEdit}
                            icon="ri-close-line"
                          />
                        )}
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>

              <div className="assigned-goals-section">
                <Typography
                  type="heading5"
                  text={
                    isEditMode
                      ? `Goals to be Updated (${goals.length})`
                      : `Goals to be Published (${goals.length})`
                  }
                  weight="bold"
                />
                <div className="goals-list">
                  {goals.length === 0 ? (
                    <div className="no-goals">
                      <Typography
                        type="small_regular"
                        text="No goals added yet. Add goals using the form on the left."
                        color="gray"
                      />
                    </div>
                  ) : (
                    goals.map((goal) => (
                      <div key={goal.id} className="goal-card">
                        <div className="goal-header">
                          <Typography
                            type="base_medium"
                            text={goal.title}
                            weight="bold"
                          />
                          <div className="goal-actions">
                            <ButtonPro
                              label="Edit"
                              type="white"
                              size="small"
                              icon="ri-edit-line"
                              onClick={() => handleEditGoal(goal)}
                            />
                            <ButtonPro
                              label="Delete"
                              type="red"
                              size="small"
                              icon="ri-delete-bin-line"
                              onClick={() => handleDeleteGoal(goal.id)}
                              disabled={deletingGoalId === goal.id}
                            />
                          </div>
                        </div>
                        <div className="goal-description">
                          <Typography
                            type="small_regular"
                            text={goal.description}
                            color="gray"
                          />
                        </div>
                        <div className="goal-details">
                          <Typography
                            type="small_regular"
                            text={`Due: ${moment.utc(goal.endDate).format('DD/MM/YYYY')}`}
                          />
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <SupervisorSelectModal
        isOpen={showSupervisorModal}
        onClose={() => setShowSupervisorModal(false)}
        onSelect={handleSupervisorSelect}
        selectedSupervisors={selectedSupervisors}
      />
    </>
  );
};
